import { BreakpointState } from '@angular/cdk/layout';
import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { OnDestroyMixin, untilComponentDestroyed } from '@w11k/ngx-componentdestroyed';
import { BehaviorSubject, Observable, map } from 'rxjs';
import { BankAccountProvider } from 'src/app/dashboard/bankAccounts/enums/bankAccountProvider.enum';
import { DialogService } from 'src/app/services/dialog.service';
import { RoutingService } from 'src/app/services/routing.service';
import { ScreenService } from 'src/app/services/screen.service';
import { BankAccountService } from 'src/app/services/shared/bank-account.service';
import { UserService } from 'src/app/services/shared/user.service';
import { SnackbarService } from 'src/app/services/snackbar.service';
import { Swiper } from 'swiper';
import { AccountType } from '../../enums/AccountType.enum';
import { UnitDepositAccountStatus } from '../../enums/UnitDepositAccountStatus.enum';
import { UnitApplicationParentType } from '../../enums/unit-bank-account/UnitApplicationParentType.enum';
import { BankAccountDto } from '../../models/cre-banking/plaid/bankAccount.model';
import { DynamicComponentItem } from '../../models/dynamic-component-loader/DynamicComponentItem.model';
import { UnitAccountLimits } from '../../models/unit-bank-account/UnitAccountLimits.model';
import { AlertDialogParams } from '../alert-dialog/alert-dialog.component';
import { ConfirmDialogParams } from '../confirm-dialog/confirm-dialog.component';
import { BankAccountNumberComponent } from '../dynamic-component-loader/bank-account-number.component';
import { UnitCardsSectionComponent } from '../unit/unit-card/unit-cards-section/unit-cards-section.component';

@Component({
  selector: 'terra-bank-account',
  templateUrl: './bank-account.component.html',
  styleUrls: ['./bank-account.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [ScreenService, BankAccountService]
})
export class BankAccountComponent extends OnDestroyMixin implements OnInit {
  @Input() account: BankAccountDto;
  @Input() bankAccountType: BankAccountProvider;

  @Input() unitParentType: UnitApplicationParentType;
  @Input() status: UnitDepositAccountStatus
  @Input() pendingBalance: number;
  @Input() accountLimits: UnitAccountLimits;
  @Input() statusNamespace: any;
  @Input() shouldShowCashSweepingDescription: boolean;
  @Input() remarkNote: boolean;

  @Input() refreshCards$: Observable<boolean>;
  @Output() onPlaidSuccess: EventEmitter<void> = new EventEmitter<void>();
  @Output() onCloseAccountSuccess: EventEmitter<void> = new EventEmitter<void>();

  @ViewChild('unitCardsSectionComponentMobile')
  cardSection: UnitCardsSectionComponent;
  AccountType = AccountType;
  BankAccountProvider = BankAccountProvider;
  isMobileOnly$: BehaviorSubject<boolean> = new BehaviorSubject(false);
  isGreaterThenMobileOnly$: BehaviorSubject<boolean> = new BehaviorSubject(false);

  organization$ = this.userService.accountDetails$.pipe(
    map((user) => user.organizationDetails)
  );

  isWireSupported: boolean;

  accountNumberHighlightText: string;

  accountNumberHighlightTextColor: string;

  constructor(private snackbarService: SnackbarService,
    private userService: UserService,
    private dialogService: DialogService,
    private screenService: ScreenService,
    private router: Router,
    private routingService: RoutingService,
    private bankAccountService: BankAccountService) {super();}

  ngOnInit(): void {
    this.isWireSupported = !!this.account.wireRoutingNumber;
    this.accountNumberHighlightText = this.isWireSupported ? `This account allows for payments through both ACH and Wire transfer.` : `Please note that payments to this account are available only via ACH. Wire payments are currently not supported.`;
    this.accountNumberHighlightTextColor = this.isWireSupported ? "#CDE3F9" : null;
  }

  ngAfterViewInit(): void {
    this.screenService.isMobileOnly().pipe(untilComponentDestroyed(this)).subscribe((state: BreakpointState) => {
      setTimeout(() => {this.isMobileOnly$.next(state.matches)});
    });
    this.screenService.isGtMobile().pipe(untilComponentDestroyed(this)).subscribe((state: BreakpointState) => {
      setTimeout(() => {this.isGreaterThenMobileOnly$.next(state.matches)});
    });
  }

  arrangeBalance(balance: number) {
    if (this.bankAccountType === BankAccountProvider.UnitAccount) {
      return balance / 100;
    }
    return balance;
  }

  tabChanged(_) {
    const swiper: Swiper = this.cardSection?.cardSlider?.swiperRef?.nativeElement?.swiper;
    swiper?.update();
  }

  snackbarMessage(itemName: string, e: Event) {
    if (!!itemName) {
      this.snackbarService.showGeneralMessage(
        `${itemName} was copied to the clipboard`,
        1
      );
    }
    e.preventDefault();
  }

  showAccountNumber(account: BankAccountDto) {
    if(!!account.mask && account.mask !== account.accountNumber.slice(-account.mask.length)){

      const dialogParams = new AlertDialogParams();

    dialogParams.title = `Account Details Restricted by Bank Policy`;
    dialogParams.description = `Due to ${account.bankName} bank's policy, we are unable to display the full routing and account numbers for your ${account.bankName} account. 
    Only the last 4 digits of your account number are visible.`;
    dialogParams.actionLabel = `Close`;

    this.dialogService
      .alertDialog(dialogParams);
      return;
    }
    const accountNumberComponent = new DynamicComponentItem(
      BankAccountNumberComponent,
      {
        accountNumber: account.accountNumber,
        name: account.accountHolderName,
        address: account.accountHolderAddress,
        achRoutingNumber: account.achRoutingNumber,
        wireRoutingNumber: account.wireRoutingNumber,
        bankName: account.bankName
      }
    );
    const dialogParams = new ConfirmDialogParams(
      'Account Details',
      null,
      'Close',
      null,
      null,
      null,
      null,
      'primary',
      accountNumberComponent,
      null,
      'right',
      this.accountNumberHighlightText
    );
    dialogParams.hideCancel = true;
    dialogParams.disableClose = true;
    if (this.accountNumberHighlightTextColor) {
      dialogParams.highlightColor = this.accountNumberHighlightTextColor
    }
    this.dialogService.confirmDialog(dialogParams);
  }

  goToEntity(account: BankAccountDto) {
    if (account.entityType === 'Business') {
      return;
    }
    this.router.navigate([account.entityType === 'Asset' ? this.routingService.gpAssetPage(Number(account.entityId)) : this.routingService.gpFundPage(Number(account.entityId))]);
  }

  FDICInfo() {
    this.bankAccountService.FDICInfo();
  }
}
